import type { Notification } from '@/api/generated/model';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNotifications } from '@/hooks/useNotifications';
import { useLanguage } from '@/i18n/language-hooks';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { Bell, CheckCheck, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getNotificationColor, getNotificationIcon } from '../notifications/notificationDisplay';

const PREVIEW_LIMIT = 5;

function CompactNotificationRow({
  notification,
  onOpen,
  onMarkAsRead,
}: {
  notification: Notification;
  onOpen: (notification: Notification) => void;
  onMarkAsRead: (id: string) => void;
}) {
  const { t } = useLanguage();
  const Icon = getNotificationIcon(notification.type);
  const iconColor = getNotificationColor(notification.type, notification.priority);

  return (
    <div className='hover:bg-accent/70 focus-within:bg-accent/70 grid w-full min-w-0 grid-cols-[1rem_minmax(0,1fr)_1.5rem] items-start gap-2 rounded-sm px-2 py-2'>
      <span className={cn('mt-0.5 shrink-0', iconColor)} aria-hidden>
        <Icon className='size-4' />
      </span>
      <button
        type='button'
        className='block min-w-0 overflow-hidden text-left outline-none'
        onClick={() => onOpen(notification)}
      >
        <span
          className={cn(
            'line-clamp-2 min-w-0 text-sm leading-tight break-words',
            !notification.isRead && 'font-semibold'
          )}
        >
          {notification.title}
        </span>
        <span className='text-muted-foreground block truncate text-xs'>{notification.message}</span>
        <span className='text-muted-foreground mt-0.5 block text-[10px]'>
          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
        </span>
      </button>
      <span className='mt-0.5 flex w-6 shrink-0 items-center justify-end'>
        {!notification.isRead ? (
          <button
            type='button'
            className='text-muted-foreground hover:text-fin-green focus-visible:ring-ring rounded-full p-0.5 outline-none focus-visible:ring-2'
            aria-label={t('userMenu.notifications.markAsRead')}
            title={t('userMenu.notifications.markAsRead')}
            onClick={(event) => {
              event.stopPropagation();
              onMarkAsRead(notification.id);
            }}
          >
            <CheckCircle2 className='size-3.5' />
          </button>
        ) : null}
      </span>
    </div>
  );
}

export function UserMenuNotificationsSub() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { notifications, unreadCount, isLoading, isMarkingAllAsRead, markAsRead, markAllAsRead } =
    useNotifications({
      limit: PREVIEW_LIMIT,
      unreadOnly: true,
    });

  const hasUnread = unreadCount > 0;

  // A side-opening submenu cannot fit beside the user menu on a phone, so on
  // mobile the entry becomes a plain item that opens the full notifications page.
  if (isMobile) {
    return (
      <DropdownMenuItem className='gap-2' onSelect={() => navigate('/notifications')}>
        <Bell
          className={cn('size-4', hasUnread ? 'text-fin-danger' : 'text-muted-foreground')}
          aria-hidden
        />
        {t('settings.tabs.notifications')}
        {hasUnread ? (
          <span className='bg-fin-danger ml-auto rounded-full px-1.5 text-xs font-medium text-white'>
            {unreadCount}
          </span>
        ) : null}
      </DropdownMenuItem>
    );
  }

  const preview = hasUnread
    ? notifications.filter((n) => !n.isRead).slice(0, PREVIEW_LIMIT)
    : notifications.slice(0, PREVIEW_LIMIT);

  const handleOpenNotification = (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      return;
    }
    navigate('/notifications');
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className='gap-2'>
        <Bell
          className={cn('size-4', hasUnread ? 'text-fin-danger' : 'text-muted-foreground')}
          aria-hidden
        />
        {t('settings.tabs.notifications')}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent
        className='max-w-[calc(100vw-2rem)] overflow-hidden p-0'
        sideOffset={4}
        style={{ width: 'min(21rem, calc(100vw - 2rem))' }}
      >
        {hasUnread ? (
          <div className='border-border border-b px-2 py-1'>
            <DropdownMenuItem
              className='text-muted-foreground focus:text-foreground gap-2 text-xs font-normal'
              disabled={isMarkingAllAsRead}
              onSelect={(event) => {
                event.preventDefault();
                markAllAsRead();
              }}
            >
              <CheckCheck className='size-3' />
              {t('userMenu.notifications.markAllRead')}
            </DropdownMenuItem>
          </div>
        ) : null}

        {isLoading ? (
          <p className='text-muted-foreground px-3 py-4 text-center text-xs'>
            {t('userMenu.notifications.loading')}
          </p>
        ) : preview.length === 0 ? (
          <p className='text-muted-foreground px-3 py-4 text-center text-xs'>
            {t('userMenu.notifications.empty')}
          </p>
        ) : (
          <div className='flex w-full min-w-0 flex-col overflow-hidden p-1'>
            {preview.map((notification) => (
              <CompactNotificationRow
                key={notification.id}
                notification={notification}
                onOpen={handleOpenNotification}
                onMarkAsRead={markAsRead}
              />
            ))}
          </div>
        )}

        <DropdownMenuSeparator className='m-0' />
        <div className='flex items-center justify-end gap-2 p-1'>
          <DropdownMenuItem
            className='flex-none text-xs'
            onSelect={() => navigate('/notifications')}
          >
            {t('userMenu.notifications.viewAll')}
          </DropdownMenuItem>
        </div>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
